import {
  addDocument,
  deleteDocument,
  getDocument,
  releaseRemovedEmailForRegistration as releaseRemovedEmailCallable
} from '../services/firebase'

export const normalizeEmailKey = (email) => String(email || '').trim().toLowerCase()

export const isUserAccountDeleted = async (uid) => {
  if (!uid) return false
  const deletedDoc = await getDocument('deletedUsers', uid)
  return deletedDoc.exists()
}

export const isEmailDeleted = async (email) => {
  const emailKey = normalizeEmailKey(email)
  if (!emailKey) return false
  const deletedDoc = await getDocument('deletedEmails', emailKey)
  return deletedDoc.exists()
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const pollUntilDeletedEmailCleared = async (emailKey, attempts = 10, delayMs = 1500) => {
  for (let i = 0; i < attempts; i += 1) {
    const deletedDoc = await getDocument('deletedEmails', emailKey)
    if (!deletedDoc.exists()) {
      return true
    }
    await wait(delayMs)
  }
  return false
}

/** Deletes leftover Firebase Auth login after admin removal so the email can register again. */
export const releaseRemovedEmailForRegistration = async (email) => {
  const emailKey = normalizeEmailKey(email)
  if (!emailKey) {
    throw new Error('INVALID_EMAIL')
  }

  const deletedDoc = await getDocument('deletedEmails', emailKey)
  if (!deletedDoc.exists()) {
    try {
      await releaseRemovedEmailCallable(email)
      return { released: true, method: 'callable' }
    } catch (error) {
      throw new Error('NOT_ELIGIBLE')
    }
  }

  const uid = deletedDoc.data()?.uid
  if (!uid) {
    throw new Error('NOT_ELIGIBLE')
  }

  await addDocument('registrationReleases', {
    email: emailKey,
    uid,
    requestedAt: new Date().toISOString()
  })

  const released = await pollUntilDeletedEmailCleared(emailKey)
  if (released) {
    return { released: true, method: 'firestore-trigger' }
  }

  try {
    await releaseRemovedEmailCallable(email)
    const releasedAfterCallable = await pollUntilDeletedEmailCleared(emailKey, 3, 1000)
    if (releasedAfterCallable) {
      return { released: true, method: 'callable' }
    }
  } catch (error) {
    console.error('Callable release fallback failed:', error)
  }

  throw new Error('RELEASE_FAILED')
}

/** Lets the same email register again after admin removal. */
export const clearDeletedEmailRecord = async (email) => {
  const emailKey = normalizeEmailKey(email)
  if (!emailKey) return

  const deletedDoc = await getDocument('deletedEmails', emailKey)
  if (deletedDoc.exists()) {
    await deleteDocument('deletedEmails', emailKey)
  }
}
